import React from 'react';
import T from 'prop-types';
// components
import { FormattedMessage, useIntl } from 'react-intl';
import ActivityActionsBar from './ActivityActionsBar/ActivityActionsBar';
import ActivityCheckupBox from './ActivityCheckupBox/ActivityCheckupBox';
import ActivityBox from './ActivityBox';
import ActivityBodyCollapse from './ActivityBodyCollapse';
import ActivityHeader from './ActivityHeader';
import ActivityHeaderBadge from './ActivityHeader/ActivityHeaderBadge';
import ActivityMentions from './ActivityMentions';
import CollapsesBox from 'components/CollapsesBox/CollapsesBox';
import Collapse from 'components/Collapse/Collapse';
import FarmFeedRowComments from 'components/FarmFeed/FarmFeedRowComments';
import FarmFeedRowAddComment from 'components/FarmFeed/FarmFeedRowAddComment';
import FormattedWeight from 'components/FormattedWeight';
import GroupInfoCollapse from 'components/Groups/GroupInfoCollapse';
import LastCommentInfo from './LastCommentInfo/LastCommentInfo';
import TreatmentPopoverList from './TreatmentPopoverList';
import Popover from 'components/Popover';
import TreatmentFarmfeedTooltip from 'components/EntityFarmfeedTooltip/TreatmentFarmfeedTooltip';
// utils
import classnames from 'classnames/bind';
import moment from 'moment';
// regional date handling
import { connect } from 'react-redux';
import { getCurrentCountryCode } from 'selectors/regionalDateSelectors';
import { formatDateForUser } from 'utils/regionalDateHelper';
// hoc
import withActivityController from './hoc/withActivityController';
// styles
import styles from './WithdrawalWarningActivity.module.scss';

const cn = classnames.bind(styles);

const affectRerenderKeys = ['id', 'updated_at', 'is_viewed', 'flagged', 'comments', 'additional_data'];

const WithdrawalWarningActivity = ({
  className,
  activity,
  currentUser,
  isChildActivity,
  markAsSeen,
  openDropdown,
  setActivityFlag,
  isMinimized,
  isTruncated,
  toggleMinimized,
  onViewFullActivity,
  defaultDropdownOptions,
  countryCode,
}) => {
  const intl = useIntl();
  const { id, pig_group, additional_data: { withdrawal_data, pg_estimated_weight, threshold_date },
    created_at, flagged, is_viewed, mentions } = activity;
  const date = moment(created_at).format('YYYY-MM-DD');
  const isAdmin = currentUser.roles_map.admin;

  return (
    <ActivityBox
      className={className}
      isFlagged={flagged}
      isTruncated={isTruncated}
      hasMarker={!isChildActivity}
      isUnseen={!is_viewed}
      markAsSeen={markAsSeen}
      onViewFullActivity={onViewFullActivity}
    >
      {!isMinimized &&
        <LastCommentInfo activity={activity} currentUser={currentUser} />
      }
      <ActivityHeader
        activityId={id}
        onOpenDropdown={(e) => openDropdown(e, defaultDropdownOptions)}
        onDoubleClick={toggleMinimized}
      >
        <ActivityHeaderBadge isCentered>
          <FormattedMessage id="general.withdrawalWarning" tagName="b" />
        </ActivityHeaderBadge>
      </ActivityHeader>
      <ActivityBodyCollapse isClosed={isMinimized}>
        <div className={cn('warning-text')}>
          <FormattedMessage
            tagName="div"
            id="component.withdrawalWarningActivity.text"
            values={{
              b: (weight) => <b>{weight}</b>,
              orange: (msg) => <span className={cn('brand-orange')}>{msg}</span>,
              blue: (msg) => <span className={cn('brand-blue')}>{msg}</span>,
              weight: <FormattedWeight value={pg_estimated_weight} />,
              treatmentsList: (
                <TreatmentPopoverList
                  treatments={withdrawal_data}
                  isCurrentUserAdmin={isAdmin}
                  hideClearanceDates
                />
              ),
            }}
          />
          <div className={cn('timestamp')}>
            {moment(created_at).format('MMM DD, YYYY, hh:mm a')}
          </div>
          <ActivityMentions isAdmin={isAdmin} users={mentions} />
        </div>

        <ActivityCheckupBox isAdmin={isAdmin} activity={activity} className={cn({ 'mb-15': isChildActivity })}>
          {pig_group && (
            <div className={cn('collapses-container')}>
              <CollapsesBox className={cn('warning-collapse-box')}>
                <GroupInfoCollapse
                  isAdmin={isAdmin}
                  titleClassName={cn('warning-group-info')}
                  group={pig_group}
                  customEndpoint={`/pig_groups/${pig_group.id}/history_dc_info?history_date=${date}`}
                  thresholdDate={threshold_date}
                />
              </CollapsesBox>
              {/* Treatment Clearance Dates Collapse - Separate Box */}
              {withdrawal_data && withdrawal_data.length > 0 && (
                <CollapsesBox className={cn('clearance-collapse-box')}>
                  <Collapse
                    className={cn('clearance-collapse')}
                    title={<span className={cn('clearance-dates-title')}>Treatment Clearance Dates</span>}
                    isCollapsed
                  >
                    <div className={cn('clearance-dates-content')}>
                      {withdrawal_data.map((treatment, index) => {
                        const renderClearanceDate = (date) => {
                          if (!date) return intl.formatMessage({ id: 'general.notAvail' });
                          const dateObj = moment(date);
                          return dateObj.isBefore(moment(), 'day')
                            ? intl.formatMessage({ id: 'general.clear' })
                            : formatDateForUser(dateObj, countryCode);
                        };

                        const truncateLimit = 30;
                        const truncatedName = treatment.name.length > truncateLimit
                          ? `${treatment.name.slice(0, truncateLimit)}...`
                          : treatment.name;
                        const clearMessage = intl.formatMessage({ id: 'general.clear' });

                        return (
                          <div key={treatment.id || index} className={cn('treatment-row')}>
                            <div className={cn('treatment-name')}>
                              <Popover
                                content={(
                                  <TreatmentFarmfeedTooltip
                                    treatmentId={treatment.id}
                                    isAdmin={isAdmin}
                                  />
                              )}
                                placement="bottomLeft"
                              >
                                <span title={treatment.name}>{truncatedName}</span>
                              </Popover>
                            </div>
                            <div className={cn('treatment-dates')}>
                              <span className={cn('clearance-item')}>
                                <span className={cn('clearance-label')}>US:</span>
                                <span className={cn('clearance-value', {
                                  clear: renderClearanceDate(treatment.us_clearance_date) === clearMessage
                                })}
                                >
                                  {renderClearanceDate(treatment.us_clearance_date)}
                                </span>
                              </span>
                              <span className={cn('clearance-item')}>
                                <span className={cn('clearance-label')}>Export:</span>
                                <span className={cn('clearance-value', {
                                  clear: renderClearanceDate(treatment.export_clearance_date) === clearMessage
                                })}
                                >
                                  {renderClearanceDate(treatment.export_clearance_date)}
                                </span>
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Collapse>
                </CollapsesBox>
              )}
            </div>
          )}
        </ActivityCheckupBox>
        {!isChildActivity && (
          <>
            <ActivityActionsBar
              activity={activity}
              currentUser={currentUser}
              openDropdown={openDropdown}
              setActivityFlag={setActivityFlag}
            />
            <FarmFeedRowComments activity={activity} />
            <FarmFeedRowAddComment activity={activity} />
          </>
        )}
      </ActivityBodyCollapse>
    </ActivityBox>
  );
};

WithdrawalWarningActivity.contextTypes = {
  router: T.object.isRequired
};

WithdrawalWarningActivity.propTypes = {
  className: T.string,
  activity: T.object.isRequired,
  currentUser: T.object.isRequired,
  markAsSeen: T.func,
  isChildActivity: T.bool,
  openDropdown: T.func.isRequired,
  setActivityFlag: T.func.isRequired,
  // from hoc
  isTruncated: T.bool.isRequired,
  isMinimized: T.bool.isRequired,
  toggleMinimized: T.func.isRequired,
  onViewFullActivity: T.func.isRequired,
  defaultDropdownOptions: T.array.isRequired,
  countryCode: T.string,
};

WithdrawalWarningActivity.defaultProps = {
  isChildActivity: false,
};

const ConnectedComponent = connect(
  (state) => ({
    countryCode: getCurrentCountryCode(state),
  })
)(WithdrawalWarningActivity);

export default withActivityController(ConnectedComponent, { affectRerenderKeys });
